<script setup lang="ts">
import {computed, PropType} from "vue";
import {useI18n} from "vue-i18n";
import {IPersonalityTraitsReportData} from "@/pages/Insights/ReportsDetailPage/reportTypes";

const {t} = useI18n();

defineProps({
  reportData: {
    type: Array as PropType<IPersonalityTraitsReportData[]>,
    required: true
  },
  loadingData: {
    type: Boolean,
    required: true
  }
});

const headers = computed(
  () => [
    {
      title: t("components.ReportsDetailPage.holland_code_headers.full_name"),
      key: "fullName"
    },
    {
      title: "Tenant",
      key: "tenant"
    },
    {
      title: t("components.ReportsDetailPage.big_5_headers.open_mindedness"),
      key: "open_mindedness"
    },
    {
      title: t("components.ReportsDetailPage.big_5_headers.conscientiousness"),
      key: "conscientiousness"
    },
    {
      title: t("components.ReportsDetailPage.big_5_headers.extraversion"),
      key: "extraversion"
    },
    {
      title: t("components.ReportsDetailPage.big_5_headers.agreeableness"),
      key: "agreeableness"
    },
    {
      title: t("components.ReportsDetailPage.big_5_headers.negative_emotionality"),
      key: "negative_emotionality"
    },
  ]
);

function getLevelLabel(percentage: number) {
  if (percentage > 75)
    return "High";

  if (percentage > 35)
    return "Medium";

  return "Low";
}

function getLevelColor(percentage: number, inverted = false) {
  if (inverted) {
    if (percentage > 75)
      return "red";

    if (percentage > 35)
      return "orange";

    return "green";
  }

  if (percentage > 75)
    return "green";

  if (percentage > 35)
    return "orange";

  return "red";
}
</script>

<template>
  <v-data-table
    :items="reportData"
    :headers="headers"
    :loading="loadingData"
    :items-per-page="50"
  >
    <template v-slot:item.fullName="{ item }">
      <span class="font-weight-bold">{{ item.fullName }}</span>

      <div>
        <div v-if="item.email">
          <v-icon size="x-small">far fa-envelope</v-icon>
          {{ item.email }}
        </div>
        <div v-if="item.phone">
          <v-icon size="x-small">far fa-phone</v-icon>
          {{ item.phone }}
        </div>
      </div>
    </template>

    <template v-slot:item.tenant="{ item }">
      <div class="py-4">
        <div class="font-weight-bold text-blue-grey-darken-1 pb-1">{{ item.tenant.title }}</div>
        <div>
          <v-chip size="x-small" class="font-weight-bold mr-3">
            <v-icon color="grey" class="mr-2">far fa-building</v-icon>
            {{ item?.institution.title }}
          </v-chip>
          <v-chip size="x-small" class="font-weight-bold">
            <v-icon color="grey" class="mr-2">far fa-graduation-cap</v-icon>
            {{ item?.cohort.title }}
          </v-chip>
        </div>
      </div>
    </template>

    <template v-slot:[`item.open_mindedness`]="{ item }: { item: IPersonalityTraitsReportData}">
      <v-chip v-if="!item['open_mindedness']" size="x-small">No Result</v-chip>
      <div v-else class="text-center font-weight-bold">
        <v-chip v-if='item["open_mindedness"]' :color='getLevelColor(item["open_mindedness"] as number)' size="x-small"
                class="font-weight-bold">
          {{ item["open_mindedness"] }}%
        </v-chip>
        <br/>
        <span :class="`text-${getLevelColor(item['open_mindedness'] as number)}`">{{
          getLevelLabel(item["open_mindedness"] as number)
        }}</span>
      </div>
    </template>

    <template v-slot:item.conscientiousness="{ item }">
      <v-chip v-if="!item['conscientiousness']" size="x-small">No Result</v-chip>
      <div v-else class="text-center font-weight-bold">
        <v-chip v-if='item["conscientiousness"]' :color='getLevelColor(item["conscientiousness"] as number)'
                size="x-small" class="font-weight-bold">
          {{ item["conscientiousness"] }}%
        </v-chip>
        <br/>
        <span :class="`text-${getLevelColor(item['conscientiousness'] as number)}`">{{
          getLevelLabel(item["conscientiousness"] as number)
        }}</span>
      </div>
    </template>

    <template v-slot:item.extraversion="{ item }">
      <v-chip v-if="!item['extraversion']" size="x-small">No Result</v-chip>
      <div v-else class="text-center font-weight-bold">
        <v-chip v-if='item["extraversion"]' :color='getLevelColor(item["extraversion"] as number)' size="x-small"
                class="font-weight-bold">
          {{ item["extraversion"] }}%
        </v-chip>
        <br/>
        <span :class="`text-${getLevelColor(item['extraversion'] as number)}`">{{
          getLevelLabel(item["extraversion"] as number)
        }}</span>
      </div>
    </template>

    <template v-slot:item.agreeableness="{ item }">
      <v-chip v-if="!item['agreeableness']" size="x-small">No Result</v-chip>
      <div v-else class="text-center font-weight-bold">
        <v-chip v-if='item["agreeableness"]' :color='getLevelColor(item["agreeableness"] as number)' size="x-small"
                class="font-weight-bold">
          {{ item["agreeableness"] }}%
        </v-chip>
        <br/>
        <span :class="`text-${getLevelColor(item['agreeableness'] as number)}`">{{
          getLevelLabel(item["agreeableness"] as number)
        }}</span>
      </div>
    </template>

    <template v-slot:item.negative_emotionality="{ item }">
      <v-chip v-if="!item['negative_emotionality']" size="x-small">No Result</v-chip>
      <div v-else class="text-center font-weight-bold">
        <v-chip v-if='item["negative_emotionality"]'
                :color='getLevelColor(item["negative_emotionality"] as number, true)' size="x-small"
                class="font-weight-bold">
          {{ item["negative_emotionality"] }}%
        </v-chip>
        <br/>
        <span :class="`text-${getLevelColor(item['negative_emotionality'] as number, true)}`">{{
          getLevelLabel(item["negative_emotionality"] as number)
        }}</span>
      </div>
    </template>

  </v-data-table>
</template>

<style scoped>

</style>
